import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Header } from './Header';
import { Pagination } from './Pagination';
import { TableHead } from './TableHead';
import { TableBody } from './TableBody';
import type {
  ColumnOrderState,
  SortingState,
  FilterFn,
  ColumnPinningState,
  Row,
} from '@tanstack/react-table';
import { TableProvider } from '../providers/TableProvider';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';
import { RowSelection, TableProps } from '../types';
import { createSelectorColumn, getTableMeta, getTotalHeadersSize, updateSorting } from '../utils';
import { DefaultCell } from './DefaultCell';
import { rankItem } from '@tanstack/match-sorter-utils';
import { BaseButton } from 'components/button';
import { Plus } from '@phosphor-icons/react';
import { omit, omitBy, size } from 'lodash';
import useLocalStorageState from 'use-local-storage-state';
import { useVirtual, useTableEvents } from '../hooks';
import NoResults from './NoResults';
import { TableLoader } from './TableLoader';
import { parseNumber } from './filter/transforms';

const minCellWidth = 96;
const maxCellWidth = 500;
const perPage = 20;
const rowHeights = { short: 44, medium: 64, tall: 84 };

export interface TypeId {
  id?: string | number;
}

export const Table = <T extends TypeId>({
  rowSelection: rowSelectionProps,
  setRowSelection: setRowSelectionProps,
  getRowId,
  isShowHeaderEmptyData = false,
  initialHidden = [],
  initialVisible = [],
  headerAddButtonProps,
  getRowCanExpand,
  renderExpandedComponent,
  expandedRowHeight,
  ...props
}: TableProps<T>) => {
  const rowHeight = props.rowHeight ? rowHeights[props.rowHeight] : rowHeights.medium;
  const initialPin = props?.config?.row?.selectable
    ? { left: ['Selector', ...(props.initialPinned || [])], right: [] }
    : props.initialPinned
      ? { left: [...(props.initialPinned || [])], right: [] }
      : undefined;

  const columnIds = props.columns?.map(col => col.id);

  const initialColumnVisibility = useMemo(() => {
    const visibility: Record<string, boolean> = {};
    // Explicitly hide columns listed in initialHidden
    for (const key of initialHidden) {
      if (key) visibility[key] = false;
    }
    if (initialVisible?.length > 0) {
      // Hide all columns not listed in initialVisible
      for (const id of columnIds) {
        if (id && !initialVisible?.includes(id)) {
          visibility[id] = false;
        }
      }
    }
    return visibility;
  }, [initialHidden, initialVisible, columnIds]);

  const [data, setData] = useState<T[]>(props.data);
  const [expanded, setExpanded] = useState({});
  const [sorting, setSorting] = useLocalStorageState<SortingState>(`${props.name}-tableSorting`, {
    defaultValue: [],
  });
  const [columnVisibility, setColumnVisibility] = useLocalStorageState(
    `${props.name}-tableColumnVisibility`,
    {
      defaultValue: initialColumnVisibility,
    }
  );
  const [columnOrder, setColumnOrder] = useLocalStorageState<ColumnOrderState>(
    `${props.name}-tableColumnOrder`,
    {
      defaultValue: [],
    }
  );
  const [columnPinning, setColumnPinning] = useLocalStorageState<ColumnPinningState>(
    `${props.name}-tableColumnPinned`,
    {
      defaultValue: initialPin,
    }
  );
  const [columnSizing, setColumnSizing] = useLocalStorageState(`${props.name}-tableColumnSizing`, {
    defaultValue: {},
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelectionInside, setRowSelectionInside] = useState<RowSelection>({});

  const isControlledRowSelection =
    rowSelectionProps !== undefined && typeof setRowSelectionProps === 'function';

  const rowSelection = useMemo(() => {
    return isControlledRowSelection ? rowSelectionProps : rowSelectionInside;
  }, [isControlledRowSelection, rowSelectionProps, rowSelectionInside]);

  const setRowSelection = useCallback(
    (updater: React.SetStateAction<RowSelection>) => {
      if (isControlledRowSelection) {
        setRowSelectionProps(updater);
      } else {
        setRowSelectionInside(updater);
      }
    },
    [isControlledRowSelection, setRowSelectionProps, setRowSelectionInside]
  );

  const columnResizeMode = 'onChange';
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const selectorColumn = createSelectorColumn<T>(
    props?.config?.row?.selectable,
    props?.config?.isExpandable,
    props?.config?.subRowsDepth
  );

  const defaultColumn = {
    minSize: minCellWidth,
    maxSize: maxCellWidth,
    cell: DefaultCell<T>,
  };

  const cols = [...selectorColumn, ...props.columns];

  const fuzzyFilter: FilterFn<T> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);

    addMeta(itemRank);

    return itemRank.passed;
  };

  useTableEvents(
    columnVisibility,
    columnOrder,
    columnPinning,
    columnSizing,
    rowSelection,
    props.config?.event
  );

  const table = useReactTable({
    getRowId,
    data: props.data,
    defaultColumn: props.defaultColumn ? props.defaultColumn : defaultColumn,
    columns: cols,
    getRowCanExpand,
    ...(props.onPaginationChange
      ? {
          manualPagination: true,
          pageCount: props.pageCount || -1,
          onPaginationChange: updater => {
            if (typeof updater !== 'function') return;
            const newPageInfo = updater(table.getState().pagination);
            props.onPaginationChange?.(newPageInfo);
          },
        }
      : {
          initialState: {
            pagination: {
              pageIndex: props.config?.pagination?.pageIndex
                ? props.config.pagination.pageIndex
                : 0,
              pageSize: props.config?.pagination ? props.config.pagination.pageSize : perPage,
            },
          },
        }),
    state: {
      expanded: {
        ...expanded,
        paginateExpandedRows: Boolean(props.onPaginationChange),
      },
      columnOrder,
      sorting,
      columnPinning,
      columnSizing,
      columnVisibility,
      globalFilter: props.meta?.tableState ? undefined : globalFilter,
      rowSelection,
      ...(props.onPaginationChange
        ? {
            pagination: props.config?.pagination,
            sorting: props.meta?.tableState?.sort
              ? [{ id: props.meta.tableState.sort, desc: props.meta.tableState.order === 'DESC' }]
              : undefined,
          }
        : {}),
    },
    columnResizeMode: 'onChange',
    enableRowSelection: row => {
      return row?.original.id !== -1;
    },
    globalFilterFn: props?.fuzzyFilter ? props.fuzzyFilter : fuzzyFilter,
    paginateExpandedRows: Boolean(props.onPaginationChange),
    getSubRows: row =>
      (row as T & { subRows: T[] })?.subRows ||
      (row as T & Record<string, unknown>)?.[props?.config?.getSubRowsKey ?? ''] ||
      [],
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: updater => {
      if (typeof updater !== 'function') return;
      const sortArray = updater(table.getState().sorting);
      const sortState = updateSorting(sortArray, props.meta?.tableState);
      if (props.config?.event) {
        props.config.event('sorting', {
          sort: sortState.sort || '',
          order: sortState.order || '',
        });
      }
      if (table.options.meta?.onTableStateChange && Boolean(props.onPaginationChange)) {
        table.options.meta?.onTableStateChange({
          sort: sortState.sort,
          order: sortState.order,
          page: null,
          cursor: null,
        });
        return;
      }
      setSorting(sortArray);
    },
    manualSorting: Boolean(props.onPaginationChange),
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onExpandedChange: setExpanded,
    onColumnPinningChange: setColumnPinning,
    onColumnSizingChange: setColumnSizing,
    meta: {
      ...getTableMeta(
        setData,
        props.meta,
        Object.keys(rowSelection).map(key => parseNumber(key))
      ),
    },
    debugTable: false,
  });

  //  table width
  const tableWidth = getTotalHeadersSize(table);

  // Get the row model for virtualization
  const rows = table.getRowModel().rows;

  // Create virtual items that include expanded rows
  const virtualRows = useMemo(() => {
    if (!renderExpandedComponent) {
      return rows;
    }
    const virtualItems: (
      | (typeof rows)[0]
      | { isExpandedRow: true; parentRow: (typeof rows)[0] }
    )[] = [];

    rows.forEach(row => {
      virtualItems.push(row);
      if (row.getIsExpanded()) {
        // Insert expanded row immediately after the parent row
        virtualItems.push({ isExpandedRow: true, parentRow: row });
      }
    });

    return virtualItems;
  }, [rows, renderExpandedComponent]);

  // Memoize the renderExpandedComponent to prevent unnecessary re-renders
  const memoizedRenderExpandedComponent = useCallback(
    (row?: Row<T>) => {
      return renderExpandedComponent?.(row);
    },
    [renderExpandedComponent]
  );

  const { getVirtualItems, getTotalSize } = useVirtual<T>(
    virtualRows,
    tableContainerRef,
    props.config?.disableVirtual,
    rowHeight,
    expandedRowHeight
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const remainingWidth = useMemo(() => {
    const width = scrollContainerRef.current?.getBoundingClientRect().width;
    if (!width) {
      return 0;
    }
    const diff = width - table.getTotalSize();
    return Math.max(diff, 0);
  }, [scrollContainerRef.current, table]);

  useEffect(() => {
    setData(props.data);
  }, [props.data]);

  return (
    <TableProvider<T>
      value={{
        config: props.config || {},
        table,
        globalFilter,
        setGlobalFilter,
        onTableStateChange: props.meta?.onTableStateChange,
      }}
    >
      <TableWrapper>
        {!props.config?.hideDefaultControlsHeader && (
          <Header
            addButtonProps={headerAddButtonProps}
            filterTitle={props?.filterTitle}
            rowSelectionCount={Object.keys(rowSelection).length}
            addButtonName={
              data.length === 0 && props.data.length === 0
                ? undefined
                : props.config?.texts?.addButtonName
            }
            tableId={props?.name}
            filters={props.filters}
          />
        )}
        <OuterContainer id="megayaee" ref={scrollContainerRef}>
          {data.length === 0 && props.data.length === 0 && !props.isLoading
            ? (() => {
                const hasActiveFilters =
                  size(
                    omitBy(
                      omit({ ...props.meta?.tableState }, ['order', 'sort']),
                      props?.excludedKeysEmptyCheck
                    )
                  ) > 0;
                const isNoResults = hasActiveFilters;
                const isNoData = !hasActiveFilters;

                return (
                  <OverLay abs>
                    <NoResults
                      type={isNoResults ? 'noResults' : 'noData'}
                      img={
                        isNoResults
                          ? props.config?.texts?.noResultsImg
                          : props.config?.texts?.noDataImg
                      }
                      title={
                        isNoResults
                          ? props.config?.texts?.noResultsTitle
                          : props.config?.texts?.noDataTitle
                      }
                      description={
                        isNoResults
                          ? props.config?.texts?.noResultsDescription
                          : props.config?.texts?.noDataDescription
                      }
                      showAction={isNoData && Boolean(props?.config?.texts?.addButtonName)}
                      addRow={isNoData ? table.options?.meta?.addRow || '' : undefined}
                      actionProps={
                        isNoData
                          ? {
                              startIcon: <Plus size={16} color="white" />,
                              children: props?.config?.texts?.addButtonName,
                              variant: 'primary',
                              size: 'md',
                            }
                          : undefined
                      }
                    />
                  </OverLay>
                );
              })()
            : null}
          <Container ref={tableContainerRef} id={props.name}>
            <TableStyles
              style={{ minWidth: tableWidth }}
              rowHeight={rowHeight}
              fillerPosition={table.getTotalSize()}
              remainingWidth={remainingWidth}
            >
              <TableHead
                columnResizeMode={columnResizeMode}
                isExpandable={props?.config?.isExpandable}
                rowHeight={rowHeight}
              />
              {props.isLoading ? (
                <TableLoader columns={table.getVisibleLeafColumns().length} />
              ) : null}
              {data.length > 0 && (
                <TableBody
                  virtualItems={getVirtualItems()}
                  totalSize={getTotalSize()}
                  rowHeight={rowHeight}
                  {...(renderExpandedComponent
                    ? { renderExpandedComponent: memoizedRenderExpandedComponent, virtualRows }
                    : {})}
                />
              )}
            </TableStyles>
            {props?.config?.texts?.addButtonName &&
            typeof table.options?.meta?.addRow !== 'string' &&
            data.length > 0 ? (
              <AddButton
                data-testid="bottom-add-button"
                rowHeight={rowHeight}
                onClick={() => {
                  table.options?.meta?.addRow('bottom');
                }}
              >
                <Plus size={16} />
                {props?.config?.texts?.addButtonName}
              </AddButton>
            ) : null}
            {props?.config?.pagination && !props.isLoading && data.length > 0 ? (
              <Pagination
                onPageChange={() => {
                  if (tableContainerRef?.current) {
                    tableContainerRef.current.scrollTo({
                      top: 0,
                      behavior: 'smooth',
                    });
                  }
                }}
              />
            ) : null}
          </Container>
        </OuterContainer>
      </TableWrapper>
    </TableProvider>
  );
};

const AddButton = styled(BaseButton)<{ rowHeight: number }>`
  position: sticky;
  left: 100px;
  color: ${p => p.theme.palette.text.tertiary};
  font-weight: 600;
  height: ${p => p.rowHeight}px;
`;
const TableWrapper = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const OuterContainer = styled.div`
  position: relative;
  overflow: hidden;
  flex-grow: 1;
  background-color: ${p => p.theme.palette.bg.primary};
  width: 100%;
  height: 100%;
`;

const OverLay = styled.div<{ abs?: boolean }>`
  position: ${p => (p.abs ? 'absolute' : 'relative')};
  height: 100%;
  width: 100%;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 24px;
`;

export const Container = styled.div`
  height: 100%;
  width: 100%;
  overflow-x: auto;
`;

export const TableStyles = styled.table<{
  fillerPosition: number;
  remainingWidth: number;
  rowHeight: number;
}>`
  width: 100%;
  border-spacing: 0px;

  .frozen {
    position: sticky;
    display: flex;
    align-items: center;
    z-index: 4;
  }
  th.frozen > div {
    width: 100%;
  }

  tr {
    display: flex;
    width: fit-content;
    height: ${p => p.rowHeight}px;

    td.selector:not(.selected) > span {
      visibility: hidden;
    }
    :hover {
      td.selector > span {
        visibility: visible;
      }
    }
  }

  tr th:last-child {
    border-top: 0;
  }
`;

export default Table;
